import { i18n } from '@goodboydigital/astro';
import { ComponentInterface, Entity, lerp } from '@goodboydigital/odie';
import { Container, Text } from 'pixi.js';
import { Signal } from 'typed-signals';

import { Shared } from '../../App';
import { CoinCounterWidget } from '../../ui/CoinCounterWidget';
import { Maths } from '../../utils/Maths';
import { VehicleEntity } from '../entities/VehicleEntity';

export class TrickComponent implements ComponentInterface
{
    public vehicle!: VehicleEntity;
    public angle = 0;
    public combo = 0;
    public accumulatedAngle = 0;
    public timeout = 0;
    public onTrickDone = new Signal<(combo: number) =>void>();
    public view: Container;
    public label: Text;
    public coinsWidget: CoinCounterWidget;
    public speedX = 0;
    public speedY = 0;

    constructor()
    {
        this.view = new Container();
        this.label = new Text(`LABEL`, {
            fontFamily: 'lubalin',
            fill: 0xFFFFFF,
            align: 'center',
            fontSize: 50,
            lineJoin: 'round',
            stroke: 'black',
            strokeThickness: 4,
        });
        this.label.anchor.set(0.5);
        this.view.addChild(this.label);

        this.coinsWidget = new CoinCounterWidget();
        this.label.addChild(this.coinsWidget.view);
        this.coinsWidget.view.y = 55;

        this.reset();
        this.trickReset();
    }

    public addedToEntity(entity: Entity): void
    {
        if (!(entity instanceof VehicleEntity)) throw new Error('Trick component only works with a VehicleEntity');
        this.vehicle = entity as VehicleEntity;
        this.vehicle.physics.physicsFixedUpdateRunner.add(this as any);
        this.vehicle.view.addChild(this.view);
        this.view.visible = false;
        this.trickReset();
    }

    public reset(): void
    {
        this.view.visible = false;
        this.trickReset();
    }

    public physicsFixedUpdate(): void
    {
        if (!this.vehicle || this.vehicle.ghost) return;

        if (this.view.visible)
        {
            if (this.vehicle.assembled)
            {
                // this.view.x = lerp(this.view.x, this.vehicle.chassis.physics.x, 0.2);
                // this.view.y = lerp(this.view.y, this.vehicle.chassis.physics.y, 0.2);
                this.view.x += this.speedX;
                this.view.y += this.speedY;
            }

            this.label.y = lerp(this.label.y, -180, 0.05);
            this.label.alpha -= 0.05;
            if (this.label.alpha <= 0)
            {
                this.view.x = 0;
                this.view.y = 0;
                this.label.alpha = 0;
                this.view.visible = false;
            }
        }

        this.trickUpdate();
    }

    private trickUpdate(): void
    {
        const angle = this.vehicle.chassis.physics.mainBody.angle;
        const angularVelocity = angle - this.angle;
        const absAngularVelocity = Math.abs(angularVelocity);
        
        if (absAngularVelocity > 0.04 && absAngularVelocity < 3 && this.vehicle.assembled)
        {
            this.accumulatedAngle += angularVelocity;
            this.timeout = 20;
        }

        if (this.timeout)
        {
            this.timeout -= 1;
            if (!this.timeout) this.trickReset();
        }

        this.angle = angle;

        if (this.vehicle.assembled)
        {
            this.evaluateTrick();
        }
        else
        {
            this.trickReset();
        }
    }

    private evaluateTrick(): void
    {
        // There is no need to be a pefect 360 - something like 300 should be elegible for a reward
        const angle = this.accumulatedAngle * 1.5;

        this.combo = Math.floor(Math.abs(angle) / Maths.PI_DOUBLE);
        if (this.combo)
        {
            console.log('[TrickComponent] Trick done:', this.combo);
            this.playTrickScoreAnim(this.combo, angle < 0);
            Shared.game.soundSystem.playSfx('trick-land');
            this.onTrickDone.emit(this.combo);
            this.trickReset();
        }
    }

    public trickReset(): void
    {
        this.combo = 0;
        this.accumulatedAngle = 0;
        this.timeout = 0;
    }

    private playTrickScoreAnim(combo: number, straight = true): void
    {
        const mode = straight
            ? i18n.translate('game_frontflip')
            : i18n.translate('game_backflip');

        this.speedX = this.vehicle.chassis.physics.mainBody.velocity[0] * 0.01;
        this.speedY = 0;
        this.view.visible = true;
        this.view.x = this.vehicle.chassis.physics.x;
        this.view.y = this.vehicle.chassis.physics.y;
        this.label.y = 0;
        this.label.text = `${mode} ${Math.round(combo * 360)}°`;
        this.label.alpha = 1.8;
        this.label.scale.set(0.75 + (combo * 0.25));
        this.view.updateTransform();
    }
}
